/**
 * SDK 登录弹窗(自写,不引 Radix/sonner)
 *
 * 复刻主流程 LoginModal 流程:手机号 + 协议 -> 获取验证码(滑块 generate/validate 拿 token)
 * -> 加密手机号 + token sendSmsCode -> 输短信码 -> encryptPhone + loginWithSms -> onLoginSuccess。
 * 差异:Radix Modal -> .hh-modal__* + createPortal 到 body 级独立 shadow(复用 modal-portal,
 *   盖住客服挂件);sonner toast -> inline 红字;@/hooks/useCountdown -> 自写;validatePhone -> 正则;
 *   useHotelStore.hotel.id -> resolveHotel();login(response) -> onLoginSuccess(writeUserStorage + 续接)。
 * 滑块验证用同弹窗内视图切换(captchaOpen),避免双层 portal。
 * 登录协调层 login-manager:openLogin(onSuccess?) 通用,本次领红包 openLogin(() => doClaim()),
 *   未来预定 openLogin(() => 跳提单) 接口预留。
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getSdkModalPortal } from './modal-portal';
import { useLoginOpen, closeLogin, onLoginSuccess } from './login-manager';
import { encryptPhone, sendSmsCode, loginWithSms } from '../api/auth';
import { generateSlideCaptcha, validateSlideCaptcha } from '../api/captcha';
import { resolveHotel } from '../api/hotel';
import LazySlideCaptcha from './LazySlideCaptcha';
import type { LazySlideCaptchaFinishData, LazySlideCaptchaRef } from '@/types/captcha';

const PHONE_RE = /^1\d{10}$/;
/** 退出动画时长(对齐 .hh-modal__card[data-state] 动画,延迟卸载) */
const CLOSE_ANIM_MS = 220;

function toDateTimeString(value: Date | string | null): string {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString();
  return value;
}

/** 自写倒计时(SDK 不引 @/hooks/useCountdown,避免额外依赖进 bundle) */
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const start = useCallback(() => {
    setSeconds(initialSeconds);
    setIsRunning(true);
  }, [initialSeconds]);
  // 渲染期守卫:倒计时到 0 停止(避开 set-state-in-effect)
  if (isRunning && seconds <= 0) {
    setIsRunning(false);
  }
  useEffect(() => {
    if (!isRunning || seconds <= 0) return;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [isRunning, seconds]);
  return { seconds, isRunning, start };
}

export function SdkLoginModal() {
  const open = useLoginOpen();
  const [portalMount] = useState(getSdkModalPortal);
  // 解耦挂载:open=true 立即挂载;open=false 延迟卸载等退出动画(H5 抽屉退场)
  const [mounted, setMounted] = useState(open);

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [captchaRequestId, setCaptchaRequestId] = useState<string | null>(null);
  const [captchaFailTip, setCaptchaFailTip] = useState('');
  const [captchaSuccessTip, setCaptchaSuccessTip] = useState('');
  const captchaRef = useRef<LazySlideCaptchaRef | null>(null);
  const { seconds, isRunning, start: startCountdown } = useCountdown(60);

  // 渲染期守卫:open 翻 true 立即挂载 + 清错(避开 set-state-in-effect)
  if (open && !mounted) {
    setMounted(true);
    setError('');
  }
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => setMounted(false), CLOSE_ANIM_MS);
      return () => clearTimeout(t);
    }
  }, [open]);

  const isPhoneValid = PHONE_RE.test(phone);
  const isFormValid = isPhoneValid && code.length === 6 && agreed && !isLoggingIn;
  const canSendCode = isPhoneValid && !isRunning && !isSendingCode;

  const resetCaptchaState = useCallback(() => {
    setCaptchaRequestId(null);
    setCaptchaFailTip('');
    setCaptchaSuccessTip('');
    captchaRef.current?.reset();
  }, []);

  const handleClose = useCallback(() => {
    setCaptchaOpen(false);
    setIsSendingCode(false);
    resetCaptchaState();
    setError('');
    closeLogin();
  }, [resetCaptchaState]);

  // 关闭按钮/遮罩:验证码视图下只关验证码(回登录表单),不关弹框;否则关弹框
  const handleCloseClick = useCallback(() => {
    if (captchaOpen) {
      setCaptchaOpen(false);
      setIsSendingCode(false);
      resetCaptchaState();
      return;
    }
    handleClose();
  }, [captchaOpen, handleClose, resetCaptchaState]);

  const generateCaptcha = useCallback(async () => {
    captchaRef.current?.startRequestGenerate();
    setCaptchaFailTip('');
    setCaptchaSuccessTip('');
    try {
      const data = await generateSlideCaptcha();
      setCaptchaRequestId(data.id);
      captchaRef.current?.endRequestGenerate(data.backgroundImage, data.sliderImage);
    } catch {
      captchaRef.current?.endRequestGenerate(null, null);
      setCaptchaOpen(false);
      setIsSendingCode(false);
      setError('安全验证加载失败，请稍后重试');
    }
  }, []);

  const sendCodeWithCaptchaToken = async (slideCaptchaToken: string) => {
    const encrypted = await encryptPhone({ input: [phone] });
    const encryptedPhone = encrypted.list[0];
    await sendSmsCode({
      phone: encryptedPhone,
      countryTelCode: '+86',
      clientType: 2,
      slideCaptchaToken,
    });
    startCountdown();
  };

  const handleSendCode = async () => {
    if (!isPhoneValid) {
      setError('请输入正确的手机号');
      return;
    }
    if (!agreed) {
      setError('请先同意服务条款和隐私政策');
      return;
    }
    setError('');
    setIsSendingCode(true);
    setCaptchaOpen(true);
    setTimeout(() => {
      generateCaptcha();
    }, 0);
  };

  const handleCaptchaFinish = async (trackData: LazySlideCaptchaFinishData) => {
    if (!captchaRequestId) {
      setError('安全验证已失效，请重新获取');
      setIsSendingCode(false);
      setCaptchaOpen(false);
      return;
    }
    captchaRef.current?.startRequestVerify();
    try {
      const response = await validateSlideCaptcha({
        id: captchaRequestId,
        track: {
          backgroundImageWidth: trackData.backgroundImageWidth,
          backgroundImageHeight: trackData.backgroundImageHeight,
          sliderImageWidth: trackData.sliderImageWidth,
          sliderImageHeight: trackData.sliderImageHeight,
          startTime: toDateTimeString(trackData.startTime),
          endTime: toDateTimeString(trackData.endTime),
          track: trackData.tracks,
        },
      });
      const slideCaptchaToken = response.token;
      const passed = response.success && response.result === 0 && slideCaptchaToken;
      if (!passed) {
        setCaptchaFailTip(
          response.result === 2
            ? '验证超时，请重新操作'
            : response.message || '验证未通过，拖动滑块将悬浮图像正确合并'
        );
        captchaRef.current?.endRequestVerify(false);
        setTimeout(generateCaptcha, 1000);
        return;
      }
      setCaptchaSuccessTip('验证通过，超过80%用户');
      captchaRef.current?.endRequestVerify(true);
      try {
        await sendCodeWithCaptchaToken(slideCaptchaToken);
        setCaptchaOpen(false);
        resetCaptchaState();
      } catch (e) {
        const err = e as Error;
        setError(err.message || '验证码发送失败，请稍后重试');
        setCaptchaOpen(false);
        resetCaptchaState();
      } finally {
        setIsSendingCode(false);
      }
    } catch {
      setCaptchaFailTip('服务异常，请稍后重试');
      captchaRef.current?.endRequestVerify(false);
      setTimeout(generateCaptcha, 1000);
    }
  };

  const handleLogin = async () => {
    if (!isFormValid) return;
    setError('');
    setIsLoggingIn(true);
    try {
      const hotel = await resolveHotel();
      const hotelId = hotel?.id;
      if (!hotelId) {
        setError('酒店信息获取失败，请刷新页面重试');
        setIsLoggingIn(false);
        return;
      }
      const encrypted = await encryptPhone({ input: [phone] });
      const encryptedPhone = encrypted.list[0];
      const response = await loginWithSms({
        phone: encryptedPhone,
        countryTelCode: '+86',
        code,
        hotelId,
        tenantId: hotel?.tenantId,
        channelType: hotel?.channelType,
      });
      // onLoginSuccess:先写 LS(让 buildContextHeaders 读到 token)再触发续接(doClaim)
      onLoginSuccess(response);
      // 重置表单(成功路径不经 handleClose)
      setPhone('');
      setCode('');
      setAgreed(false);
      setCaptchaOpen(false);
    } catch (err) {
      const e = err as Error;
      setError(e.message || '登录失败，请检查验证码是否正确');
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (!portalMount || !mounted) return null;

  const content = (
    <div
      className="hh-modal__backdrop"
      data-state={open ? 'open' : 'closed'}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseClick();
      }}
      role="presentation"
    >
      <div className="hh-modal__card hh-modal__card--login">
        <button
          type="button"
          className="hh-login__close"
          onClick={handleCloseClick}
          aria-label="关闭"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M18 6L6 18M6 6L18 18" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {captchaOpen ? (
          <div className="hh-login__captcha">
            <h2 className="hh-login__captcha-title">安全验证</h2>
            <LazySlideCaptcha
              ref={captchaRef}
              width={300}
              height={180}
              showRefresh
              failTip={captchaFailTip}
              successTip={captchaSuccessTip}
              onFinish={handleCaptchaFinish}
              onRefresh={generateCaptcha}
            />
          </div>
        ) : (
          <div className="hh-login__body">
            <div className="hh-login__title">手机登录</div>
            <div className="hh-login__form">
              <div className="hh-login__field">
                <input
                  type="tel"
                  placeholder="请输入您的手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                  maxLength={11}
                  className="hh-login__input"
                />
              </div>
              <div className="hh-login__field">
                <input
                  type="text"
                  placeholder="请输入验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  className="hh-login__input"
                />
                <button
                  type="button"
                  disabled={!canSendCode}
                  onClick={handleSendCode}
                  className="hh-login__send"
                >
                  {isRunning ? `${seconds}秒后重试` : '获取验证码'}
                </button>
              </div>
              <div className="hh-login__agree">
                <button
                  type="button"
                  aria-label="同意用户协议"
                  aria-pressed={agreed}
                  onClick={() => setAgreed(!agreed)}
                  className={agreed ? 'hh-login__check hh-login__check--on' : 'hh-login__check'}
                >
                  {agreed && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4L3.5 6.5L9 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
                <span>登录或创建账户即代表您同意服务条款和隐私政策</span>
              </div>
              {error && <p className="hh-login__error">{error}</p>}
              <button
                type="button"
                disabled={!isFormValid}
                onClick={handleLogin}
                className="hh-login__submit"
              >
                {isLoggingIn ? '登录中...' : '立即登录'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, portalMount);
}
